// Seeding a report's quick filters from the route query (item 4, task 2.2; decision 6).
//
// A dashboard drill opens /reports/<reportName>?<colField>=<ids>&<dateField>=<period>, and the
// report must arrive already filtered so its total is the figure on the widget that was clicked.
// The reports screen holds quick-filter state as three arrays **index-aligned with the report's
// columns** (Reports.vue: quickFiltersSelection / datesFrom / datesTo, mirrored into Vuex per
// report index), and builds its request body from them, so this turns a query into exactly those
// arrays. Reports.vue applies them before its first fetch.
//
// Shapes, per column type, as getDataFromSource reads them (Reports.vue:727-847):
//   dropdown  -> an array of option objects, each carrying the column's filterFldId ({ id: 3 })
//   Date      -> { dateModifierFunc: 'last30days' }, or 'custom' with dd/mm/yyyy in datesFrom/To
//   Number    -> a number in datesFrom/datesTo (Currency too)
//   Yes/No    -> { <last segment of colField>: 'Yes' | 'No' }
//
// Anything outside the backend's vocabulary is dropped rather than filtered on, so a stale or
// hand-edited link opens the report unfiltered on that dimension instead of erroring.

const IDS = /^\d+(,\d+)*$/
const WORD = /^[A-Za-z][A-Za-z0-9]*$/
const DATE = /^(\d{4})[/-](\d{2})[/-](\d{2})$/
const NUMBER = /^-?\d+(\.\d+)?$/

// Read by the screen itself, not a column filter.
const RESERVED = ['search', 'sortFieldId', 'sortOrder']

function text(value) {
  if (Array.isArray(value)) value = value[0]
  return value === null || value === undefined ? '' : String(value).trim()
}

// The payload key for a column: the first segment of a dotted colField, as the screen posts it.
function keyFor(column) {
  return column && column.colField ? String(column.colField).split('.')[0] : ''
}

function lastSegment(column) {
  const parts = column && column.colField ? String(column.colField).split('.') : []
  return parts.length ? parts[parts.length - 1] : ''
}

function isDate(column) {
  return column && column.colType === 'Date'
}

function isRange(column) {
  return column && (column.colType === 'Number' || column.colType === 'Currency')
}

// yyyy/MM/dd -> dd/MM/yyyy, which is what the screen's masked inputs hold.
function toUkDate(value) {
  const match = DATE.exec(value)
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null
}

// True when the query says anything about this report's own filters or ordering. A plain visit
// from the menu, and `?search=` on its own, must leave today's behaviour untouched.
export function queryNamesAnyFilter(query, columns) {
  const q = query || {}
  const named = Object.keys(q).filter(key => text(q[key]) !== '')
  if (!named.length) return false

  if (named.some(key => key === 'sortFieldId' || key === 'sortOrder')) return true

  return (columns || []).some(column => {
    const key = keyFor(column)
    if (!key) return false
    return named.includes(key) || named.includes(`${key}From`) || named.includes(`${key}To`)
  })
}

// One dropdown value: the ids as the option objects the payload maps over.
function selectionForDropdown(column, raw) {
  const field = column.filterFldId || keyFor(column)
  // A `*`-prefixed static list holds its own values rather than ids.
  const isStaticList = !!column.filterEP && String(column.filterEP).charAt(0) === '*'
  if (isStaticList) return WORD.test(raw) || IDS.test(raw) ? [{ [field]: raw }] : null
  if (IDS.test(raw)) return raw.split(',').map(id => ({ [field]: Number(id) }))
  return null
}

export function seedFromQuery(query, columns) {
  const q = query || {}
  const cols = columns || []
  const seed = {
    selection: cols.map(() => null),
    datesFrom: cols.map(() => null),
    datesTo: cols.map(() => null),
    sortField: '',
    sortFieldId: null,
    sortOrder: 0,
    seeded: false
  }

  cols.forEach((column, index) => {
    const key = keyFor(column)
    if (!key || RESERVED.includes(key)) return

    const value = text(q[key])
    const from = text(q[`${key}From`])
    const to = text(q[`${key}To`])

    if (isDate(column)) {
      if (value && WORD.test(value) && value !== 'custom') {
        seed.selection[index] = { dateModifierFunc: value }
        seed.seeded = true
        return
      }
      const ukFrom = from ? toUkDate(from) : null
      const ukTo = to ? toUkDate(to) : null
      if (ukFrom || ukTo) {
        seed.selection[index] = { dateModifierFunc: 'custom' }
        seed.datesFrom[index] = ukFrom
        seed.datesTo[index] = ukTo
        seed.seeded = true
      }
      return
    }

    if (isRange(column)) {
      if (from && NUMBER.test(from)) {
        seed.datesFrom[index] = Number(from)
        seed.seeded = true
      }
      if (to && NUMBER.test(to)) {
        seed.datesTo[index] = Number(to)
        seed.seeded = true
      }
      return
    }

    if (column.filterEP === 'YES/NO') {
      if (value === 'true' || value === 'false') {
        seed.selection[index] = { [lastSegment(column)]: value === 'true' ? 'Yes' : 'No' }
        seed.seeded = true
      }
      return
    }

    if (value) {
      const selection = selectionForDropdown(column, value)
      if (selection) {
        seed.selection[index] = selection
        seed.seeded = true
      }
    }
  })

  // The ordering, for widgets that are defined by it (TOP10QUOTES, TOP10CUST).
  const sortFieldId = text(q.sortFieldId)
  const sortOrder = text(q.sortOrder)
  if (IDS.test(sortFieldId) && (sortOrder === '1' || sortOrder === '-1')) {
    const column = cols.find(c => c && Number(c.id) === Number(sortFieldId))
    if (column) {
      seed.sortFieldId = Number(sortFieldId)
      seed.sortOrder = Number(sortOrder)
      seed.sortField = keyFor(column)
      seed.seeded = true
    }
  }

  return seed
}

// Once a column's option list has loaded, swap a seeded stand-in for the real option object, so
// the filter dialog shows the label ("Active", "Last 24 hours") rather than a bare id. Matching
// is by the same key the MultiSelect compares on (its dataKey, the column's filterFldId).
export function matchSeededSelection(selected, options, column) {
  if (!selected || !options || !options.length) return selected
  const field = (column && column.filterFldId) || keyFor(column)

  if (Array.isArray(selected)) {
    return selected.map(value => {
      const match = options.find(option => option && String(option[field]) === String(value[field]))
      return match || value
    })
  }
  if (selected.dateModifierFunc) {
    const match = options.find(option => option && option.dateModifierFunc === selected.dateModifierFunc)
    return match || selected
  }
  return selected
}
